import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
    X, Lock, Eye, EyeOff, LogOut, Save, Trash2, Plus, Upload, Loader2, CalendarDays, Image as ImageIcon,
} from "lucide-react";
import { api, imgUrl } from "../api";
import { useSite } from "../context/SiteContext";

const uid = () => (window.crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
const CATEGORIES = ["Cosmetología", "Barbería", "Estética", "Spa"];
const TABS = [
    { id: "contenido", label: "Contenido" },
    { id: "servicios", label: "Servicios" },
    { id: "galeria", label: "Galería" },
    { id: "testimonios", label: "Testimonios" },
    { id: "citas", label: "Citas" },
];

const compress = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const max = 1600;
                const scale = Math.min(1, max / Math.max(img.width, img.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.82));
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const inputCls =
    "w-full rounded-lg border border-gold/20 bg-white/5 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-gold/60 focus:outline-none transition-colors";

const Field = ({ label, value, onChange, type = "text", placeholder, testid }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <input
            data-testid={testid}
            type={type}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} mt-1.5`}
        />
    </label>
);

const Area = ({ label, value, onChange, rows = 3, testid, hint }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <textarea
            data-testid={testid}
            rows={rows}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} mt-1.5 resize-none`}
        />
        {hint && <span className="block mt-1 text-[10px] text-zinc-500">{hint}</span>}
    </label>
);

export default function AdminPanel() {
    const { adminOpen, setAdminOpen, isAdmin, login, logout, content, refresh } = useSite();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("contenido");
    const [draft, setDraft] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadCat, setUploadCat] = useState("Cosmetología");
    const [editLang, setEditLang] = useState("es");
    const [bookings, setBookings] = useState([]);
    const fileRef = useRef(null);

    useEffect(() => {
        if (adminOpen && content) setDraft(JSON.parse(JSON.stringify(content)));
    }, [adminOpen, content]);

    useEffect(() => {
        if (adminOpen && isAdmin && tab === "citas") {
            api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
        }
    }, [adminOpen, isAdmin, tab]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await login(email, password);
            setEmail("");
            setPassword("");
            toast.success("¡Bienvenida de nuevo!");
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Correo o contraseña incorrectos");
        } finally {
            setBusy(false);
        }
    };

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const bk = (k) => (editLang === "en" ? `${k}_en` : k);
    const bGet = (k) => draft?.[bk(k)] ?? "";
    const bSet = (k, v) => set(bk(k), v);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/content", draft);
            await refresh();
            toast.success("Cambios guardados en el sitio");
        } catch {
            toast.error("No se pudieron guardar los cambios");
        } finally {
            setBusy(false);
        }
    };

    const uploadImages = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const items = [];
            for (const f of files) {
                const data = await compress(f);
                const { data: res } = await api.post("/admin/images", { data });
                items.push({ id: uid(), url: res.url, category: uploadCat, caption: "" });
            }
            setDraft((d) => ({ ...d, gallery: [...(d.gallery || []), ...items] }));
            toast.success(`${items.length} imagen(es) añadida(s) — pulsa "Guardar cambios"`);
        } catch {
            toast.error("Error al procesar las imágenes");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const uploadSingle = async (e, key) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const data = await compress(f);
            const { data: res } = await api.post("/admin/images", { data });
            set(key, res.url);
            toast.success("Imagen actualizada — pulsa \"Guardar cambios\"");
        } catch {
            toast.error("Error al procesar la imagen");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const patchGallery = (id, patch) =>
        setDraft((d) => ({ ...d, gallery: d.gallery.map((g) => (g.id === id ? { ...g, ...patch } : g)) }));
    const removeGallery = (id) =>
        setDraft((d) => ({ ...d, gallery: d.gallery.filter((g) => g.id !== id) }));

    const patchService = (id, patch) =>
        setDraft((d) => ({ ...d, services: d.services.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
    const removeService = (id) =>
        setDraft((d) => ({ ...d, services: d.services.filter((s) => s.id !== id) }));
    const addService = () =>
        setDraft((d) => ({
            ...d,
            services: [...d.services, { id: uid(), category: "Cosmetología", name: "Nuevo servicio", description: "", price: "", duration: "" }],
        }));

    const patchTestimonial = (id, patch) =>
        setDraft((d) => ({ ...d, testimonials: d.testimonials.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    const removeTestimonial = (id) =>
        setDraft((d) => ({ ...d, testimonials: d.testimonials.filter((t) => t.id !== id) }));
    const addTestimonial = () =>
        setDraft((d) => ({ ...d, testimonials: [...d.testimonials, { id: uid(), name: "Cliente", text: "", rating: 5 }] }));

    const deleteBooking = async (id) => {
        try {
            await api.delete(`/admin/bookings/${id}`);
            setBookings((b) => b.filter((x) => x.id !== id));
            toast.success("Cita eliminada");
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    return (
        <AnimatePresence>
            {!isAdmin && adminOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
                    onClick={() => setAdminOpen(false)}
                    data-testid="admin-login-overlay"
                >
                    <motion.form
                        onSubmit={handleLogin}
                        initial={{ scale: 0.94, y: 18, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.96, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-sm rounded-2xl border border-gold/25 bg-[#121215] p-8 shadow-2xl"
                        data-testid="admin-login-form"
                    >
                        <div className="flex flex-col items-center text-center">
                            <span className="h-14 w-14 rounded-full bg-gold/10 flex items-center justify-center text-gold">
                                <Lock size={22} />
                            </span>
                            <h2 className="mt-5 font-serif text-2xl text-amber-50">Panel de administración</h2>
                            <p className="mt-2 text-xs text-zinc-500 font-light">Introduce tus datos para gestionar el sitio</p>
                        </div>
                        <div className="mt-6">
                            <label className="block">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Correo electrónico</span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="admin-login-email"
                                    placeholder="tu@correo.com"
                                    autoComplete="username"
                                    className={`${inputCls} mt-1.5`}
                                    autoFocus
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Contraseña</span>
                                <span className="relative block">
                                    <input
                                        type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="admin-login-input"
                                        placeholder="Contraseña"
                                        autoComplete="current-password"
                                        className={`${inputCls} pr-11 mt-1.5`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw(!showPw)}
                                        data-testid="admin-login-toggle-password"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-gold"
                                        aria-label="Mostrar contraseña"
                                    >
                                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </span>
                            </label>
                        </div>
                        {err && (
                            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">
                                {err}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={busy || !password}
                            data-testid="admin-login-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold-gradient text-black font-semibold px-6 py-3 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
                            {busy ? "Verificando..." : "Entrar"}
                        </button>
                    </motion.form>
                </motion.div>
            )}

            {isAdmin && adminOpen && draft && (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed inset-0 z-[90] flex flex-col bg-[#0A0A0B]"
                    data-testid="admin-panel"
                >
                    <div className="glass border-b border-gold/20 px-4 sm:px-8 py-4 flex items-center justify-between gap-4 shrink-0">
                        <div className="flex items-center gap-3">
                            <img src={imgUrl(draft.logo_url)} alt="Logo" className="h-9 w-9 rounded-full object-cover ring-1 ring-gold/40" />
                            <div>
                                <p className="font-serif text-lg text-amber-50 leading-none">Modo administración</p>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gold mt-1">{draft.business_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                            <button
                                onClick={() => setAdminOpen(false)}
                                data-testid="admin-close"
                                className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs uppercase tracking-widest text-gold-light hover:bg-gold/10 transition-colors"
                            >
                                <X size={13} /> Ver sitio
                            </button>
                            <button
                                onClick={logout}
                                data-testid="admin-logout"
                                className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-xs uppercase tracking-widest text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                                <LogOut size={13} /> Salir
                            </button>
                        </div>
                    </div>

                    <div className="px-4 sm:px-8 py-4 flex gap-2 overflow-x-auto shrink-0 border-b border-gold/10">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`admin-tab-${t.id}`}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition-all border ${
                                    tab === t.id
                                        ? "bg-gold-gradient text-black border-transparent font-semibold"
                                        : "border-gold/20 text-zinc-300 hover:border-gold/50"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6 pb-32">
                            {tab === "contenido" && (
                                <>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Idioma de edición:</span>
                                        {["es", "en"].map((l) => (
                                            <button
                                                key={l}
                                                onClick={() => setEditLang(l)}
                                                data-testid={`admin-edit-lang-${l}`}
                                                className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-all ${
                                                    editLang === l
                                                        ? "bg-gold-gradient text-black border-transparent font-semibold"
                                                        : "border-gold/25 text-zinc-300 hover:border-gold/50"
                                                }`}
                                            >
                                                {l === "es" ? "Español" : "English"}
                                            </button>
                                        ))}
                                        {editLang === "en" && (
                                            <span className="text-[10px] text-zinc-500">Si dejas un campo vacío, se mostrará el texto en español</span>
                                        )}
                                    </div>
                                    {editLang === "es" && (
                                        <div className="grid sm:grid-cols-2 gap-5">
                                            <Field label="Nombre del negocio" value={draft.business_name} onChange={(v) => set("business_name", v)} testid="admin-input-business-name" />
                                            <Field label="Calificación Google" value={draft.google_rating} onChange={(v) => set("google_rating", v)} testid="admin-input-google-rating" />
                                        </div>
                                    )}
                                    {editLang === "es" ? (
                                        <Field label="Lema (marquesina)" value={bGet("tagline")} onChange={(v) => bSet("tagline", v)} testid="admin-input-tagline" hint="Separa palabras con ·" />
                                    ) : (
                                        <Field label="Lema (marquesina) en inglés" value={bGet("tagline")} onChange={(v) => bSet("tagline", v)} testid="admin-input-tagline-en" hint="Separa palabras con ·" />
                                    )}
                                    {editLang === "es" ? (
                                        <Area label="Título principal (hero)" value={bGet("hero_title")} onChange={(v) => bSet("hero_title", v)} rows={2} testid="admin-input-hero-title" hint="Usa saltos de línea para separar frases. La 2ª línea se muestra dorada." />
                                    ) : (
                                        <Area label="Título principal (hero) en inglés" value={bGet("hero_title")} onChange={(v) => bSet("hero_title", v)} rows={2} testid="admin-input-hero-title-en" hint="La 2ª línea se muestra dorada." />
                                    )}
                                    {editLang === "es" ? (
                                        <Area label="Subtítulo (hero)" value={bGet("hero_subtitle")} onChange={(v) => bSet("hero_subtitle", v)} rows={2} testid="admin-input-hero-subtitle" />
                                    ) : (
                                        <Area label="Subtítulo (hero) en inglés" value={bGet("hero_subtitle")} onChange={(v) => bSet("hero_subtitle", v)} rows={2} testid="admin-input-hero-subtitle-en" />
                                    )}
                                    {editLang === "es" ? (
                                        <Field label="Anuncio destacado" value={bGet("announcement")} onChange={(v) => bSet("announcement", v)} testid="admin-input-announcement" />
                                    ) : (
                                        <Field label="Anuncio destacado en inglés" value={bGet("announcement")} onChange={(v) => bSet("announcement", v)} testid="admin-input-announcement-en" />
                                    )}
                                    {editLang === "es" && (
                                        <Field label="Título sección servicios" value={bGet("about_title")} onChange={(v) => bSet("about_title", v)} testid="admin-input-about-title" />
                                    )}
                                    {editLang === "en" && (
                                        <Field label="Título sección servicios en inglés" value={bGet("about_title")} onChange={(v) => bSet("about_title", v)} testid="admin-input-about-title-en" />
                                    )}
                                    {editLang === "es" ? (
                                        <Area label="Descripción del negocio" value={bGet("about_text")} onChange={(v) => bSet("about_text", v)} rows={3} testid="admin-input-about-text" />
                                    ) : (
                                        <Area label="Descripción del negocio en inglés" value={bGet("about_text")} onChange={(v) => bSet("about_text", v)} rows={3} testid="admin-input-about-text-en" />
                                    )}
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <Field label="Teléfono mostrado" value={draft.phone} onChange={(v) => set("phone", v)} testid="admin-input-phone" />
                                        <Field label="WhatsApp (código país + número)" value={draft.whatsapp} onChange={(v) => set("whatsapp", v)} testid="admin-input-whatsapp" hint="Ej: 19143825653" />
                                    </div>
                                    <Field label="Dirección" value={draft.address} onChange={(v) => set("address", v)} testid="admin-input-address" />
                                    <Field label="Link de Google Maps" value={draft.map_link} onChange={(v) => set("map_link", v)} testid="admin-input-map-link" />
                                    <Field label="Cantidad de reseñas Google" value={draft.google_count ?? "7"} onChange={(v) => set("google_count", v)} testid="admin-input-google-count" />
                                    <div className="grid sm:grid-cols-2 gap-6 pt-2">
                                        {[
                                            { key: "logo_url", label: "Logo del negocio", testid: "admin-upload-logo" },
                                            { key: "hero_bg", label: "Fondo del hero", testid: "admin-upload-hero-bg" },
                                        ].map(({ key, label, testid }) => (
                                            <div key={key} className="rounded-xl border border-gold/20 bg-surface p-4">
                                                <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                                                <div className="mt-3 flex items-center gap-4">
                                                    <img src={imgUrl(draft[key])} alt={label} className="h-16 w-16 rounded-lg object-cover ring-1 ring-gold/30" />
                                                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs uppercase tracking-widest text-gold-light hover:bg-gold/10 transition-colors">
                                                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                                                        Cambiar
                                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSingle(e, key)} data-testid={testid} />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {tab === "servicios" && (
                                <>
                                    <button
                                        onClick={addService}
                                        data-testid="admin-add-service"
                                        className="inline-flex items-center gap-2 rounded-full bg-gold-gradient text-black font-semibold px-5 py-2.5 text-sm hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-shadow"
                                    >
                                        <Plus size={15} /> Añadir servicio
                                    </button>
                                    {draft.services.map((s) => (
                                        <div key={s.id} className="rounded-xl border border-gold/20 bg-surface p-5" data-testid={`admin-service-${s.id}`}>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <Field label="Nombre" value={s.name} onChange={(v) => patchService(s.id, { name: v })} testid={`admin-service-name-${s.id}`} />
                                                <label className="block">
                                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Categoría</span>
                                                    <select value={s.category} onChange={(e) => patchService(s.id, { category: e.target.value })} data-testid={`admin-service-category-${s.id}`} className={`${inputCls} mt-1.5 [&>option]:bg-[#121215]`}>
                                                        {CATEGORIES.map((c) => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <Field label="Duración" value={s.duration} onChange={(v) => patchService(s.id, { duration: v })} testid={`admin-service-duration-${s.id}`} />
                                                <div className="sm:col-span-2">
                                                    <Area label="Descripción" value={s.description} onChange={(v) => patchService(s.id, { description: v })} rows={2} testid={`admin-service-description-${s.id}`} />
                                                </div>
                                            </div>
                                            <div className="grid sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gold/10">
                                                <Field label="Nombre (English)" value={s.name_en ?? ""} onChange={(v) => patchService(s.id, { name_en: v })} testid={`admin-service-name-en-${s.id}`} />
                                                <div className="sm:col-span-2">
                                                    <Area label="Descripción (English)" value={s.description_en ?? ""} onChange={(v) => patchService(s.id, { description_en: v })} rows={2} testid={`admin-service-description-en-${s.id}`} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeService(s.id)}
                                                data-testid={`admin-delete-service-${s.id}`}
                                                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                <Trash2 size={13} /> Eliminar servicio
                                            </button>
                                        </div>
                                    ))}
                                </>
                            )}

                            {tab === "galeria" && (
                                <>
                                    <div className="rounded-xl border border-gold/20 bg-surface p-5 flex flex-col sm:flex-row sm:items-end gap-4">
                                        <label className="block flex-1">
                                            <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Categoría para nuevas imágenes</span>
                                            <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="admin-upload-category" className={`${inputCls} mt-1.5 [&>option]:bg-[#121215]`}>
                                                {CATEGORIES.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient text-black font-semibold px-6 py-2.5 text-sm hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-shadow">
                                            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                            {uploading ? "Subiendo..." : "Subir imágenes"}
                                            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} data-testid="admin-gallery-upload-input" />
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {draft.gallery.map((g) => (
                                            <div key={g.id} className="rounded-xl border border-gold/20 bg-surface overflow-hidden" data-testid={`admin-gallery-item-${g.id}`}>
                                                <div className="relative">
                                                    <img src={imgUrl(g.url)} alt={g.caption || "Imagen"} className="w-full h-36 object-cover" />
                                                    <button
                                                        onClick={() => removeGallery(g.id)}
                                                        data-testid={`admin-delete-image-${g.id}`}
                                                        className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 border border-red-500/50 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                                                        aria-label="Eliminar imagen"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                    <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-[0.2em] bg-black/70 text-gold px-2 py-1 rounded-full">{g.category}</span>
                                                </div>
                                                <div className="p-3 space-y-2">
                                                    <input value={g.caption} onChange={(e) => patchGallery(g.id, { caption: e.target.value })} placeholder="Pie de foto" data-testid={`admin-gallery-caption-${g.id}`} className={inputCls} />
                                                    <select value={g.category} onChange={(e) => patchGallery(g.id, { category: e.target.value })} data-testid={`admin-gallery-category-${g.id}`} className={`${inputCls} [&>option]:bg-[#121215]`}>
                                                        {CATEGORIES.map((c) => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {draft.gallery.length === 0 && (
                                        <p className="text-center text-zinc-500 font-light py-10 flex flex-col items-center gap-3">
                                            <ImageIcon size={28} className="text-gold/40" />
                                            No hay imágenes. Sube las primeras fotos de tu negocio.
                                        </p>
                                    )}
                                </>
                            )}

                            {tab === "testimonios" && (
                                <>
                                    <button
                                        onClick={addTestimonial}
                                        data-testid="admin-add-testimonial"
                                        className="inline-flex items-center gap-2 rounded-full bg-gold-gradient text-black font-semibold px-5 py-2.5 text-sm hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-shadow"
                                    >
                                        <Plus size={15} /> Añadir testimonio
                                    </button>
                                    {draft.testimonials.map((t) => (
                                        <div key={t.id} className="rounded-xl border border-gold/20 bg-surface p-5" data-testid={`admin-testimonial-${t.id}`}>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <Field label="Nombre del cliente" value={t.name} onChange={(v) => patchTestimonial(t.id, { name: v })} testid={`admin-testimonial-name-${t.id}`} />
                                                <Field label="Estrellas (1-5)" type="number" value={t.rating} onChange={(v) => patchTestimonial(t.id, { rating: Math.max(1, Math.min(5, Number(v) || 5)) })} testid={`admin-testimonial-rating-${t.id}`} />
                                                <div className="sm:col-span-2">
                                                    <Area label="Reseña" value={t.text} onChange={(v) => patchTestimonial(t.id, { text: v })} rows={2} testid={`admin-testimonial-text-${t.id}`} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeTestimonial(t.id)}
                                                data-testid={`admin-delete-testimonial-${t.id}`}
                                                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                <Trash2 size={13} /> Eliminar
                                            </button>
                                        </div>
                                    ))}
                                    {draft.testimonials.length === 0 && (
                                        <p className="text-center text-zinc-500 font-light py-10">Aún no hay testimonios. Añade las reseñas de tus clientes.</p>
                                    )}
                                </>
                            )}

                            {tab === "citas" && (
                                <>
                                    {bookings.length > 0 ? (
                                        bookings.map((b) => (
                                            <div key={b.id} className="rounded-xl border border-gold/20 bg-surface p-5 flex items-start justify-between gap-4" data-testid={`admin-booking-${b.id}`}>
                                                <div className="min-w-0">
                                                    <p className="font-serif text-lg text-amber-100">
                                                        {b.name} · <span className="text-gold-light">{b.phone}</span>
                                                    </p>
                                                    <p className="mt-1 text-sm text-zinc-400 font-light">
                                                        {b.service && `${b.service} — `}
                                                        {b.date && `${b.date} `}
                                                        {b.time && `${b.time}`}
                                                    </p>
                                                    {b.message && <p className="mt-2 text-sm text-zinc-500 font-light italic">“{b.message}”</p>}
                                                    <p className="mt-2 text-[10px] uppercase tracking-widest text-zinc-600">
                                                        Recibida: {new Date(b.created_at).toLocaleString("es-ES")}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => deleteBooking(b.id)}
                                                    data-testid={`admin-delete-booking-${b.id}`}
                                                    className="shrink-0 h-9 w-9 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                                                    aria-label="Eliminar cita"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-center text-zinc-500 font-light py-10 flex flex-col items-center gap-3">
                                            <CalendarDays size={28} className="text-gold/40" />
                                            No hay solicitudes de cita todavía.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="glass border-t border-gold/20 px-4 sm:px-8 py-4 flex items-center justify-between gap-4 shrink-0">
                        <p className="text-xs text-zinc-500 font-light hidden sm:block">Los cambios se aplican al sitio inmediatamente al guardar.</p>
                        <button
                            onClick={save}
                            disabled={busy}
                            data-testid="admin-save-button"
                            className="inline-flex items-center gap-2.5 bg-gold-gradient text-black font-semibold px-8 py-3 rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.45)] transition-all disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {busy ? "Guardando..." : "Guardar cambios"}
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
